import type { Maybe, OptionalPromise } from '@adornis/base/utilTypes.js';
import type { Renderable } from '@adornis/functional-lit/renderable.js';
import { stateful, useEffect, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import type { AdornisUser } from '@adornis/users/db/a-user.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';

export interface DashboardAction<U extends AdornisUser = AdornisUser> {
  name: string;
  /** Literal iconify class, e.g. `icon-[material-symbols--payments-rounded]` (Tailwind only generates literal class tokens). */
  icon?: string;
  iconAlternative?: Renderable;
  /**
   * Theme colour pair for the icon chip, as literal classes: `bg-success text-success-content`. The `-content` half
   * keeps the icon legible on light colours like warning.
   */
  color: string;
  show: (user: Maybe<U>, ...args: unknown[]) => OptionalPromise<boolean>;
  action: (user: Maybe<U>, ...args: unknown[]) => OptionalPromise<void>;
}

const ActionCard = stateful((props: { user: Maybe<AdornisUser>; action: DashboardAction; args: unknown[] }) => {
  return html`
    <!-- the tile is the card: glass, sheen and the hover lift come from the design package -->
    <button
      type="button"
      class="card adornis-focus-ring group relative isolate flex min-h-56 w-full cursor-pointer flex-col items-center justify-center gap-5 overflow-hidden px-6 py-7 text-center sm:w-52 md:w-84"
      @click=${() => props.action.action(props.user, ...props.args)}
    >
      <!-- the action's colour as light behind the glass, centred under the icon; brightens under the pointer -->
      <span
        class="pointer-events-none absolute top-1/2 left-1/2 -z-10 size-44 -translate-x-1/2 -translate-y-2/3 rounded-full opacity-10 blur-3xl transition-opacity duration-300 group-hover:opacity-25 motion-reduce:transition-none ${props.action.color}"
      ></span>

      <span
        class="flex size-16 items-center justify-center rounded-box shadow-sm transition-transform duration-200 ease-out group-hover:scale-105 motion-reduce:transition-none ${props.action.color}"
      >
        ${
          props.action.icon
            ? html`<span class="${props.action.icon} size-9"></span>`
            : (props.action.iconAlternative ?? nothing)
        }
      </span>

      <div class="flex flex-col items-center gap-2">
        <span class="font-semibold leading-tight select-none md:text-lg">${props.action.name}</span>
        <span
          class="h-1 w-8 rounded-full opacity-40 transition-[width,opacity] duration-200 ease-out group-hover:w-20 group-hover:opacity-70 motion-reduce:transition-none ${props.action.color}"
        ></span>
      </div>
    </button>
  `;
});

export const ActionGrid = stateful((props: { actions: DashboardAction[]; onEmpty?: () => void; args?: unknown[] }) => {
  const user = useObservable(() => currentUser<AdornisUser>(), []);
  const [activeActions, setActiveActions] = useState<DashboardAction[]>([]);

  useEffect(() => {
    async function filterObjects(user: Maybe<AdornisUser>, ...args: unknown[]) {
      const results = await Promise.all(
        props.actions.map(async action => ({
          action,
          visible: await action.show(user, ...args),
        })),
      );
      const visibleSections = results.filter(result => result.visible);
      if (visibleSections.length === 0 && props.onEmpty) {
        props.onEmpty();
        return;
      }
      setActiveActions(visibleSections.map(result => result.action));
    }
    void filterObjects(user, ...(props.args ?? []));
  }, [user]);

  return html`
    <div class="flex flex-wrap justify-center gap-4 md:gap-5">
      ${repeat(
        activeActions,
        action => action.name,
        action =>
          ActionCard({
            user,
            action,
            args: props.args ?? [],
          }),
      )}
    </div>
  `;
});
